import React, { useMemo, useRef, useState } from 'react';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import DashboardBlock from './DashboardBlock';
import { Delete, FormatListBulletedOutlined, Search } from '@material-ui/icons';
import UserActivityDialog from '../dialogs/UserActivityDialog';
import InputAdornment from '@material-ui/core/InputAdornment';
import { sortLastNamesDesc } from '../../scripts/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import Pagination from '@material-ui/lab/Pagination';
import { removeUserFromAllProjects } from '../../models/api/subscribers';
import { useSelector } from 'react-redux';
import { getUserState } from '../../features/user/selectors';
import CircularLoader from '../loader/CircularLoader';
import { useHistory, useParams } from 'react-router';
import { ISubscriber } from '../../api-client/autogenerated';

export type UserRow = {
  id: string;
  name: string;
  company: string;
  email: string;
  projects: { id: string; name: string }[];
  disabled?: boolean | null;
  isRegistered?: boolean | null;
  isSiteAdmin?: boolean | null;
  adminOfSubscriberId?: string | null;
};

type Props = {
  users: UserRow[];
  subscriber: ISubscriber | null;
  refreshSubscriber: () => void;
};

const useStyles = makeStyles(() =>
  createStyles({
    text: {
      fontSize: '14px',
    },
    icon: {
      fill: '#B2B1B2',
    },
    sortIcon: {
      display: 'flex',
      flexDirection: 'column',
      height: '22px',
      paddingTop: '1px',
      marginLeft: '8px',
    },
    sortIconEnabled: {
      color: '#616061',
    },
    sortIconDisabled: {
      color: '#B2B1B2',
    },
    disableDefaultSortIcon: {
      display: 'none',
    },
    tablePagination: {
      display: 'flex',
      justifyContent: 'center',
      color: '#2C69D6',
    },
  }),
);

export default function UsersTable(props: Props) {
  const classes = useStyles();
  const { users, subscriber, refreshSubscriber } = props;

  const user = useSelector(getUserState);
  const { subscriberId } = useParams<{ subscriberId: string }>();

  const history = useHistory();

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState<string>();
  const [orderBy, setOrderBy] = useState<'name' | 'company'>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const [selectedUser, setSelectedUser] = useState<UserRow>();
  const [userActivityDialogOpen, setUserActivityDialogOpen] = useState(false);
  const [removingUser, setRemovingUser] = useState<UserRow>();

  const tableRef = useRef<HTMLTableElement>(null);

  const filteredUsers = useMemo(() => {
    if (!searchInput) return users;
    return users.filter((user) => {
      const formattedInput = searchInput.toLowerCase();
      return (
        user.name.toLowerCase().includes(formattedInput) ||
        user.email.toLowerCase().includes(formattedInput)
      );
    });
  }, [users, searchInput]);

  const handleSort = (newOrderBy: 'name' | 'company') => {
    if (newOrderBy === orderBy) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(newOrderBy);
      setOrder('desc');
    }
  };

  const handleProjectSelect = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    history.push(`/main/projects/${event.target.value as string}`);
  };

  const handleRemoveUser = async (userToRemove: UserRow) => {
    const proceed = window.confirm(
      `Are you sure you want to remove ${userToRemove.name} from all ${
        subscriber ? subscriber.name : 'of your'
      } projects?`,
    );
    if (!proceed) return;
    try {
      setRemovingUser(userToRemove);
      await removeUserFromAllProjects(subscriberId, userToRemove.id);
      refreshSubscriber();
    } finally {
      setRemovingUser(undefined);
    }
  };

  return (
    <DashboardBlock title="Users">
      <TextField
        size="small"
        variant="outlined"
        placeholder="Search Users"
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          setPage(0);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search
                fontSize="small"
                classes={{
                  root: classes.icon,
                }}
              />
            </InputAdornment>
          ),
        }}
        style={{ margin: '8px 0px 8px 8px' }}
      />
      <TableContainer component={Paper} style={{ overflowX: 'visible' }}>
        <Table ref={tableRef} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  onClick={() => handleSort('name')}
                  classes={{ icon: classes.disableDefaultSortIcon }}
                >
                  Name
                  <div className={classes.sortIcon}>
                    <FontAwesomeIcon
                      icon={faCaretUp}
                      style={{ marginBottom: '-3px' }}
                      className={
                        orderBy === 'name' ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      style={{ marginTop: '-3px' }}
                      className={
                        orderBy === 'name' ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                  </div>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  onClick={() => handleSort('company')}
                  classes={{ icon: classes.disableDefaultSortIcon }}
                >
                  Company
                  <div className={classes.sortIcon}>
                    <FontAwesomeIcon
                      icon={faCaretUp}
                      style={{ marginBottom: '-3px' }}
                      className={
                        orderBy === 'company' ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      style={{ marginTop: '-3px' }}
                      className={
                        orderBy === 'company' ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                  </div>
                </TableSortLabel>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Projects</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers
              .sort((a, b) => {
                if (orderBy === 'name') {
                  return order === 'desc'
                    ? sortLastNamesDesc(a.name, b.name)
                    : -sortLastNamesDesc(a.name, b.name);
                } else {
                  return order === 'desc'
                    ? a.company.localeCompare(b.company)
                    : b.company.localeCompare(a.company);
                }
              })
              .slice(page * pageSize, page * pageSize + pageSize)
              .map((row) => (
                <TableRow
                  key={row.id}
                  style={{
                    backgroundColor: row.disabled
                      ? '#dcdcdc'
                      : row.isRegistered === false
                      ? '#ffd3d3'
                      : 'none',
                  }}
                >
                  {row.disabled === true ? (
                    <TableCell className={classes.text} style={{ width: '20%' }}>
                      <Typography>
                        {row.name}
                        <span style={{ color: 'red', fontSize: 14 }}> [Disabled]</span>
                      </Typography>
                    </TableCell>
                  ) : row.isRegistered === false ? (
                    <TableCell className={classes.text} style={{ width: '20%' }}>
                      <Typography>
                        {row.name}
                        <span style={{ color: 'red', fontSize: 14 }}> [Not Registered]</span>
                      </Typography>
                    </TableCell>
                  ) : (
                    <TableCell className={classes.text} style={{ width: '20%' }}>
                      <Typography>{row.name}</Typography>
                    </TableCell>
                  )}
                  <TableCell className={classes.text}>
                    <Typography>{row.company}</Typography>
                  </TableCell>
                  <TableCell className={classes.text} style={{ width: '20%' }}>
                    <Typography>{row.email}</Typography>
                  </TableCell>
                  <TableCell className={classes.text} style={{ width: '20%' }}>
                    <Select
                      variant="outlined"
                      defaultValue={`${row.projects.length} Projects`}
                      renderValue={(value) => value}
                      onChange={handleProjectSelect}
                      displayEmpty
                      fullWidth
                      MenuProps={{ style: { marginTop: 48 } }}
                      style={{ height: '48px' }}
                    >
                      {row.projects.map(({ id, name }) => (
                        <MenuItem key={row.id + name} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="center" style={{ width: '20%' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 16,
                      }}
                    >
                      <Tooltip arrow placement="top" title="View Activity Log">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(row);
                            setUserActivityDialogOpen(true);
                          }}
                          style={{ padding: 0 }}
                        >
                          <FormatListBulletedOutlined />
                        </IconButton>
                      </Tooltip>
                      {removingUser !== row ? (
                        <Tooltip arrow placement="top" title="Remove User">
                          <IconButton
                            onClick={() => handleRemoveUser(row)}
                            style={{
                              padding: 0,
                              visibility:
                                row.isSiteAdmin ||
                                row.id === user.id ||
                                row.adminOfSubscriberId === subscriberId
                                  ? 'hidden'
                                  : 'visible',
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <CircularLoader size={24} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 64px',
          marginTop: 16,
          paddingBottom: 16,
        }}
      >
        <div />
        <Pagination
          count={Math.ceil(filteredUsers.length / pageSize)}
          shape="rounded"
          color="primary"
          showFirstButton
          showLastButton
          page={page + 1}
          onChange={(_, newPage) => {
            const scrollY = window.scrollY;
            const tableHeight = tableRef.current?.clientHeight || 0;

            setPage(newPage - 1);

            setTimeout(() => {
              const newTableHeight = tableRef.current?.clientHeight || 0;
              window.scrollTo({
                top: scrollY + (newTableHeight - tableHeight),
                behavior: 'instant',
              });
            }, 0);
          }}
          className={classes.tablePagination}
        />
        <FormControl>
          <FormLabel>Rows per page</FormLabel>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(e.target.value as number);
              setPage(0);
            }}
            style={{ marginTop: 4 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </div>
      {selectedUser && (
        <UserActivityDialog
          open={userActivityDialogOpen}
          handleClose={() => setUserActivityDialogOpen(false)}
          userId={selectedUser.id}
          name={selectedUser.name}
        />
      )}
    </DashboardBlock>
  );
}
